<template>
    <portal :append-to="append">
        <div class="modal-container center">
            <div v-if="visible" class="modal-bg"></div>
            <transition :name="transition">
                <div v-if="visible" class="modal-wrapper">
                    <dialog class="modal" :class="mods" aria-modal="true">
                        <div class="modal-close-button" @click="close('cancel')">
                            <vz-icon v-tooltip.left="$t('action.close')" variant="slim">
                                close
                            </vz-icon>
                        </div>
                        <div v-if="!hasSidebar" class="modal-title">
                            <slot name="title"></slot>
                        </div>
                        <div class="modal-content">
                            <slot :close="close"></slot>
                        </div>
                        <template v-if="$slots.actions">
                            <div class="modal-actions">
                                <slot name="actions"></slot>
                            </div>
                        </template>
                    </dialog>
                </div>
            </transition>
        </div>
    </portal>
</template>

<script setup lang="ts">
    import { useModalStore } from '@/stores/modal.store';

    const emit = defineEmits<{
        close: [ModalCloseParams];
    }>();

    export type SingleMod = 'mod-info' | 'mod-settings' | 'mod-sidebar-layout';

    export type ErrorMods = ['mod-message', 'mod-error'];
    export type InfoMods = ['mod-message', 'mod-info'];
    export type SuccessMods = ['mod-message', 'mod-success'];

    export type MessageMods = ErrorMods | InfoMods | SuccessMods;

    export type ModalMods = SingleMod | MessageMods | SingleMod[];

    export type ModalCloseParams = {
        id: string;
        type: 'cancel' | 'submit' | 'submit-cancel';
    };

    type Props = {
        id: string;
        visible?: boolean;
        append?: string;
        transition?: string;
        mods?: ModalMods;
    };

    const props = withDefaults(defineProps<Props>(), {
        visible: false,
        sidebar: false,
        settings: false,
        append: '#dialog-outlet',
        transition: 'vz-dialog',
        mods: undefined,
    });

    const modalStore = useModalStore();

    const close = (reason: ModalCloseParams['type'] = 'cancel', _event?: MouseEvent) => {
        modalStore.closeTopModal();
        emit('close', { id: props.id, type: reason });
    };

    const keys = useMagicKeys();
    whenever(
        () => keys.escape,
        () => {
            if (modalStore.isTopModal(props.id)) {
                close('cancel');
            }
        },
    );

    const hasSidebar = computed(() => {
        if (!props.mods) {
            return false;
        }

        if (isSingleMod(props.mods)) {
            return props.mods === 'mod-sidebar-layout';
        }

        if (isArrayOfSingleMods(props.mods)) {
            return props.mods.includes('mod-sidebar-layout');
        }
        return false;
    });

    function isSingleMod(mod: ModalMods): mod is SingleMod {
        return typeof mod === 'string';
    }

    function isArrayOfSingleMods(mods: ModalMods): mods is SingleMod[] {
        return Array.isArray(mods);
    }

    defineExpose({ close });
</script>

<style lang="scss">
    /** @NOTE this is not scoped on purpose. The children are outside of the tree (teleport) */
    .vz-dialog-enter-active {
        transition: all 150ms cubic-bezier(0, 0, 0.2, 1);
    }

    .vz-dialog-leave-active {
        transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .vz-dialog-enter-from,
    .vz-dialog-leave-to {
        opacity: 0;
        transform: scale(0.7);
    }

    @keyframes shake-x {
        0%,
        100% {
            transform: translateZ(0);
        }

        10%,
        30%,
        50%,
        70%,
        90% {
            transform: translate3d(-4px, 0, 0);
        }

        20%,
        40%,
        60%,
        80% {
            transform: translate3d(4px, 0, 0);
        }
    }

    .modal-container {
        align-items: center;
        -webkit-app-region: initial;
        display: flex;
        height: 100%;
        justify-content: center;
        left: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: var(--layer-modal);

        &:not(:empty) {
            pointer-events: all;
        }
    }

    body {
        --dialog-width: 560px;
        --dialog-max-width: 80vw;
        --dialog-max-height: 85vh;
        --modal-background: var(--grey-100);
        --modal-width: 90vw;
        --modal-height: 85vh;
        --modal-max-width: 1100px;
        --modal-max-height: 1000px;
        --modal-max-width-narrow: 800px;
        --modal-border-width: 1px;
        --modal-radius: var(--radius-l);
        --modal-community-sidebar-width: 280px;
        --modal-border-color: var(--grey-300);
        --modal-shadow-color: var(--primary-900);
        --background-modifier-cover: var(--primary-700) / 15%;
        --shadow-s:
            0 1px 2px hsl(from hsl(var(--modal-shadow-color)) h s l / 2.8%),
            0 3.4px 6.7px hsl(from hsl(var(--modal-shadow-color)) h s l / 4.2%),
            0 15px 30px hsl(from hsl(var(--modal-shadow-color)) h s l / 7%);
        --shadow-m: hsl(from hsl(var(--modal-shadow-color)) h s l / 10%) 0 4px 12px;
        --shadow-l:
            0 1.8px 7.3px hsl(from hsl(var(--modal-shadow-color)) h s l / 7.1%),
            0 6.3px 24.7px hsl(from hsl(var(--modal-shadow-color)) h s l / 11.2%),
            0 30px 90px hsl(from hsl(var(--modal-shadow-color)) h s l / 20%);
    }
</style>

<style lang="scss">
    .modal-close-button {
        border-radius: var(--radius-s);
        color: hsl(var(--primary-700));
        cursor: pointer;
        display: grid;
        font-size: 24px;
        height: 24px;
        place-items: center;
        position: absolute;
        right: var(--size-2-3);
        top: var(--size-2-3);
        width: 24px;

        &:hover,
        &:focus {
            background-color: hsl(var(--background-modifier-hover));
            color: hsl(var(--primary-900));

            @apply transition-300;
        }
    }

    .modal-title {
        color: hsl(var(--primary-400));
        font-size: var(--font-ui-large);
        font-weight: var(--font-semibold);
        line-height: var(--line-height-tight);
        text-align: left;
    }

    .modal {
        --checkbox-size: var(--font-ui-medium);

        background-color: hsl(var(--modal-background));
        border: var(--modal-border-width) solid hsl(var(--modal-border-color));
        border-radius: var(--modal-radius);
        box-shadow: var(--shadow-m);
        display: flex;
        flex-direction: column;
        gap: 1rem;
        max-height: var(--dialog-max-height);
        max-width: var(--dialog-max-width);
        min-height: 100px;
        overflow: auto;
        padding: 1.5rem;
        position: relative;
        transition: none !important;
        width: var(--dialog-width);

        @apply z-50;

        .modal-content {
            flex: 1 1 auto;
            font-size: var(--font-ui-medium);

            p:not(.help-message) {
                margin-block: 0 0.5rem;
            }
        }

        .modal-actions {
            display: flex;
            flex-direction: row;
            gap: 1rem;
            justify-content: flex-end;
        }

        &.mod-sidebar-layout {
            display: flex;
            flex-direction: column;
            height: var(--modal-height);
            max-height: var(--modal-max-height);
            max-width: var(--modal-max-width);
            overflow: hidden;
            padding: 0;
            width: var(--modal-width);

            .modal-content {
                display: flex;
            }
        }

        &.mod-settings {
            .modal-content {
                margin-block-start: 0;
                overflow: hidden;
            }
        }

        &.mod-error {
            --message-modal-background: hsl(from hsl(var(--color-red)) h s 12%);
            --message-modal-border: hsl(from hsl(var(--color-red)) h s 20%);
            --message-modal-color: hsl(from hsl(var(--color-red)) 5deg s 85%);
        }

        &.mod-info {
            --message-modal-background: hsl(from hsl(var(--color-blue)) h s 12%);
            --message-modal-border: hsl(from hsl(var(--color-blue)) h s 20%);
            --message-modal-color: hsl(from hsl(var(--color-blue)) h s 85%);
        }

        &.mod-success {
            --message-modal-background: hsl(from hsl(var(--color-green)) h s 8%);
            --message-modal-border: hsl(from hsl(var(--color-green)) h s 15%);
            --message-modal-color: hsl(from hsl(var(--color-green)) h s 85%);
        }

        &.mod-message {
            background-color: var(--message-modal-background);
            border-color: var(--message-modal-border);
            color: var(--message-modal-color);

            .modal-close-button {
                color: var(--message-modal-color);
            }
        }
    }

    .modal-bg {
        backdrop-filter: blur(2px);
        background-color: hsl(var(--background-modifier-cover));
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
</style>
